// Footage-video dedup + storage-path identity (multi-video upload, Phase 1).
//
// Mirrors photoUpload/photoKeys.js exactly, on the same shared builder, so the
// two ingest flows can never drift on sanitization or truncation. The videoKey
// becomes the storage object path's last segment AND the Firestore doc id in
// projects/{id}/videos, so the SAME physical file must produce the SAME key on
// every upload or dedup fails and we double-store a multi-GB clip.
//
// Shape: `${lastModifiedSeconds}-${size}-${contentTag}-${sanitizedName}`, where
// contentTag is a hash of sampled file bytes (see buildVideoContentTag below
// for why videos need it and photos do not). The tag is dropped when the bytes
// cannot be read, leaving the photo-style `${seconds}-${size}-${name}`.
//
// WHY lastModified and not a probed capture time: a browser cannot read
// creation metadata out of an MP4 (and definitely not out of Brinno's
// MJPEG-in-MP4), so the file's mtime is the only capture-ish timestamp
// available client-side. WHOLE SECONDS is what makes it safe as an identity:
// browsers re-stamp File.lastModified with ~1 ms of jitter on each folder drop,
// which is what broke photo dedup before #323 truncated to seconds.

import { MAX_OBJECT_KEY_LENGTH, buildObjectKey, sanitizeFileName as sanitize } from '../upload/objectKeys';

export const MAX_VIDEO_KEY_LENGTH = MAX_OBJECT_KEY_LENGTH;

// The `videos/{uid}/{projectId}/` prefix is ALSO where the single-source-video
// flow uploads (`videos/{uid}/{projectId}/{fileName}`), and that object path is
// what fires `analyze_video_motion` — the trigger that claims the project's one
// `video` slot and dispatches a full motion analysis. Footage items must not do
// that, so they live one segment deeper, under `footage/`.
//
// Segment count, not a name prefix, is what separates them: a legacy upload is
// always exactly 4 path segments and a footage item always exactly 5, so no
// user-chosen filename can ever be mistaken for the other kind. See
// `_parse_footage_path` / the length guard in
// packages/functions/storage/background/main.py.
export const FOOTAGE_PATH_SEGMENT = 'footage';

export function sanitizeVideoFileName(name) {
  return sanitize(name, { fallback: 'video' });
}

// How much of each end of the file feeds the content tag below. 64 KB reads in
// microseconds off local disk and comfortably covers a container header plus
// the first frames, so two clips from the same shoot differ inside it.
export const CONTENT_SAMPLE_BYTES = 64 * 1024;

// WHY the video key samples CONTENT and the photo key does not.
//
// A photo key is safe on metadata alone because its timestamp is per-frame EXIF
// capture time. A video has no browser-readable capture metadata, so the key
// falls back to the file's mtime — and all three metadata components collide in
// exactly the Brinno case this feature exists for:
//
//   * size — the camera splits at a fixed byte boundary, so chunks are
//     byte-identical in LENGTH;
//   * name — folder rollover restarts numbering, so 100BRNZO/TLC00001.AVI and
//     101BRNZO/TLC00001.AVI both exist;
//   * mtime — a bulk SD-card copy stamps every file with the same second
//     (ordering.js already treats that as an expected condition).
//
// Two genuinely different clips then share a key, and the second is reported as
// "already in this project" and never uploaded — a clip silently missing from
// the finished timelapse, with no error anywhere. The directory would
// disambiguate them, but `webkitRelativePath` is empty for every file the
// picker yields (it is a plain multi-file input, not `webkitdirectory`), so
// content is the only discriminator actually available client-side.
async function readSlice(file, start, end) {
  try {
    const blob = file.slice(start, end);
    if (blob && typeof blob.arrayBuffer === 'function') {
      return new Uint8Array(await blob.arrayBuffer());
    }
    if (typeof FileReader !== 'function') return null;
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(new Uint8Array(reader.result));
      reader.onerror = () => resolve(null);
      reader.readAsArrayBuffer(blob);
    });
  } catch (_e) {
    return null;
  }
}

// Head + tail of the file. Two ranged reads rather than the whole object: these
// are multi-GB files and the preflight must stay interactive.
async function sampleBytes(file) {
  if (!file || typeof file.slice !== 'function') return null;
  const size = Number.isFinite(file.size) ? file.size : 0;
  if (size <= 0) return null;
  const head = await readSlice(file, 0, Math.min(CONTENT_SAMPLE_BYTES, size));
  if (!head || head.length === 0) return null;
  if (size <= CONTENT_SAMPLE_BYTES) return [head];
  const tail = await readSlice(file, size - CONTENT_SAMPLE_BYTES, size);
  return tail && tail.length > 0 ? [head, tail] : [head];
}

// FNV-1a, run twice with different offset bases for a 64-bit tag. Not a
// cryptographic hash and does not need to be — it separates a hundred clips in
// one shoot, where a 64-bit space makes an accidental collision unreachable.
// Plain arithmetic rather than crypto.subtle so it works identically off a
// secure context and under jsdom.
function fnv1a(chunks, seed) {
  let hash = seed >>> 0;
  for (const bytes of chunks) {
    for (let i = 0; i < bytes.length; i += 1) {
      hash ^= bytes[i];
      hash = Math.imul(hash, 0x01000193) >>> 0;
    }
  }
  return hash >>> 0;
}

// A stable hex tag for the file's content, or null when the bytes are
// unreadable (a File-like without `slice`, a zero-byte file, a revoked
// handle). Null degrades the key to its metadata-only form — the pre-existing
// behaviour — rather than throwing and failing the whole scan.
export async function buildVideoContentTag(file) {
  const chunks = await sampleBytes(file);
  if (!chunks) return null;
  const lo = fnv1a(chunks, 0x811c9dc5).toString(16).padStart(8, '0');
  const hi = fnv1a(chunks, 0x9e3779b1).toString(16).padStart(8, '0');
  return `${lo}${hi}`;
}

export async function buildVideoKey(file) {
  if (!file) throw new Error('buildVideoKey: file is required');
  return buildObjectKey(file, {
    stableAtMs: Number.isFinite(file.lastModified) ? file.lastModified : undefined,
    maxLength: MAX_VIDEO_KEY_LENGTH,
    fallbackName: 'video',
    discriminator: await buildVideoContentTag(file),
  });
}

export function videoStoragePath(uid, projectId, videoKey) {
  return `videos/${uid}/${projectId}/${FOOTAGE_PATH_SEGMENT}/${videoKey}`;
}

// Server-written poster frame for the footage item (one JPEG per video).
export function videoPosterPath(uid, projectId, videoKey) {
  return `video_posters/${uid}/${projectId}/${videoKey}.jpg`;
}
